import React, { forwardRef } from 'react';
import styled from 'styled-components';
import { background, border, color, layout, position, shadow, space, typography, compose } from 'styled-system';
import { prop, theme } from 'styled-tools';

import Icon from '@ui/icons/Icon.tsx';
import { createSystemPropFilter } from '@utils/styled-component-tools.ts';

import { useCSSColors } from '../../hooks/useCSSColors.ts';

const system = compose(background, border, color, layout, position, shadow, space, typography);

const StyledFloatingActionButton = styled('button').withConfig({
  shouldForwardProp: createSystemPropFilter(system),
})<{ $iconTapColor?: string }>`
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: ${theme('shadows.md')};
  padding: 0;
  width: 40px;
  height: 40px;
  border: none;
  border-radius: ${theme('radii.full')};
  background-color: ${theme('colors.background')};
  -webkit-tap-highlight-color: rgba(0, 0, 0, 0);

  &:focus-visible {
    outline-offset: 2px;
  }
  &:active {
    outline: none;
    border: none;
    box-shadow: ${theme('shadows.lg')};
    svg {
      fill: ${prop('$iconTapColor', theme('colors.primaryText'))};
    }
  }
  &:hover {
    cursor: pointer;
  }

  /* Styled System Overrides Everything always comes last */
  ${system}
`;

type FloatingActionButtonProps = React.ComponentPropsWithoutRef<'button'> & {
  ariaLabel?: string;
  degreesOfIconRotation?: number;
  iconColor?: string;
  iconName: string;
  iconTapColor?: string;
};

const FloatingActionButton = forwardRef<HTMLButtonElement, FloatingActionButtonProps>(
  ({ ariaLabel, degreesOfIconRotation, iconColor, iconName, iconTapColor, ...rest }, ref) => {
    const colors = useCSSColors();

    return (
      <StyledFloatingActionButton
        data-testid="FloatingActionButton"
        ref={ref}
        aria-label={ariaLabel || (typeof rest.children === 'string' && rest.children) || ''}
        $iconTapColor={iconTapColor}
        {...rest}
      >
        <Icon
          data-testid="Icon"
          name={iconName}
          iconSize="md"
          color={iconColor || colors.secondaryText}
          degreesOfIconRotation={degreesOfIconRotation}
          aria-hidden="true"
          focusable="false"
        />
      </StyledFloatingActionButton>
    );
  },
);

FloatingActionButton.displayName = 'Floating Action Button';

export default FloatingActionButton;
